import { formatNumberWithSuffix } from "~/utils/formatting"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { legendDot, legendSwatch } from "~/utils/graphUtils"

import { Tooltip } from "../../Tooltip"
import {
  type MixedChartData,
  type CategoryChartSeries,
  type DateChartSeries,
  type PieChartData,
} from "../SuperChart"
import { truncate } from "lodash"
import { extractColor } from "~/utils/graphUtils"
import { getTailwindValue } from "~/utils/tailwind"
import { cn } from "~/utils/helpers"

export function generateDateCategoryChartLegend(
  data: DateChartSeries[] | CategoryChartSeries[],
  compact: boolean | undefined,
  onClick: (entry: DateChartSeries | CategoryChartSeries) => void,
  /** Wrap onto further lines, right-aligned, instead of one scrolling line (chart toolbar row). */
  wrap = false,
) {
  return (
    <div
      className={cn(
        "flex flex-row max-w-full",
        wrap ? "flex-wrap justify-end gap-y-1" : "overflow-x-auto overflow-y-hidden h-5",
        compact ? "gap-x-2" : "gap-x-3",
      )}
      style={wrap ? undefined : { scrollbarWidth: "none" }}
    >
      {data
        .filter((entry) => !("hideInLegend" in entry && entry.hideInLegend))
        .map((entry) => {
          const legendColor = entry.disabled
            ? getTailwindValue("--theme-fg-disabled")
            : extractColor(entry.colors?.[0] ?? getTailwindValue("--theme-fg-base"))

          return (
            <Tooltip title={entry.custom?.legendTooltip ?? ""} key={entry.name}>
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  onClick(entry)
                }}
                className={`group flex flex-row ${wrap ? "" : "w-full"} gap-2 cursor-pointer text-nowrap line-through ${entry.disabled ? "" : "decoration-transparent"} items-center justify-between transition-all`}
              >
                {legendSwatch(entry.name, legendColor, {
                  dashed: !!entry.dashStyle && entry.dashStyle !== "Solid",
                  disabled: entry.disabled,
                })}
              </div>
            </Tooltip>
          )
        })}
    </div>
  )
}

export function generateMixedChartLegend(
  data: MixedChartData[],
  compact: boolean | undefined,
  onClick: (entry: MixedChartData) => void,
) {
  return (
    <div
      className={`flex flex-row overflow-x-auto overflow-y-hidden max-w-full h-5 ${compact ? "gap-2" : "gap-3"}`}
      style={{
        scrollbarWidth: "none",
      }}
    >
      {data.map((entry: any) => {
        const legendColor = extractColor(
          entry.colors?.[0] ?? entry.color ?? getTailwindValue("--theme-fg-base"),
        )

        return (
          <Tooltip title={entry.custom?.legendTooltip ?? ""} key={entry.name}>
            <div
              onClick={(e) => {
                e.stopPropagation()
                onClick(entry)
              }}
              className={`group flex flex-row w-full gap-2 cursor-pointer text-nowrap line-through ${entry.disabled ? "" : "decoration-transparent"} items-center justify-between transition-all`}
            >
              {legendSwatch(entry.name, legendColor, {
                dashed: !!entry.dashStyle && entry.dashStyle !== "Solid",
                disabled: entry.disabled,
              })}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

export function generateDoughnutChartLegend(
  data: PieChartData[],
  dataAsPercentage: boolean | undefined,
  compact: boolean | undefined,
  hasTitle: boolean | undefined,
  onClick: (entry: PieChartData) => void,
) {
  return (
    <div
      style={{ justifyContent: "safe center" }}
      className={`flex flex-col grow max-w-64 max-h-[200px] overflow-y-auto ${compact ? "gap-1" : "gap-2"} ${hasTitle ? "mt-5" : "mt-0"}`}
    >
      {data.map((entry) => {
        return (
          <Tooltip title={entry.custom?.legendTooltip ?? ""} key={entry.name}>
            <div
              onClick={(e) => {
                e.stopPropagation()
                onClick(entry)
              }}
              className={`flex flex-row w-full gap-2 cursor-pointer text-nowrap line-through ${entry.disabled ? "text-fg-muted hover:text-fg-base" : "text-fg-base decoration-transparent"} text-md items-center justify-between transition-all`}
            >
              <div className="flex flex-row gap-2 items-center">
                {legendDot(entry.disabled ? getTailwindValue("--color-fg-disabled") : entry.color)}
                <div className="label-sm">
                  {entry.custom?.truncate
                    ? truncate(entry.name, { length: entry.custom?.length ?? 27 })
                    : entry.name}
                </div>
              </div>
              {!entry.custom?.dontShowData && (
                <div className="font-medium">
                  {dataAsPercentage
                    ? (entry.data * 100).toLocaleString(getNumberFormatLocale(), {
                        maximumFractionDigits: 1,
                      }) + "%"
                    : formatNumberWithSuffix(entry.data, 2)}
                </div>
              )}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}
